import React, { useImperativeHandle } from 'react';
import { millisToSec } from 'utils/time';
import {
  SegmentedVideoAddEventListener,
  SegmentedVideoPlayerInstance,
  SegmentedVideoRemoveEventListener,
} from './types';

export type UseSegmentedVideoPlayerInstanceProps = {
  addEventListener: SegmentedVideoAddEventListener;
  getDurationMillis: () => number;
  maxBufferedPlaybackPosition: () => number;
  getCurrentTime: () => number;
  setCurrentTime: (sec: number) => void;
  videoRef: React.RefObject<HTMLVideoElement>;
  instanceRef: React.ForwardedRef<SegmentedVideoPlayerInstance>;
  removeEventListener: SegmentedVideoRemoveEventListener;
  seekToFrameAccurate: (logicalTimeSec: number) => Promise<number>;
};

export default function useSegmentedVideoPlayerInstance({
  addEventListener,
  getDurationMillis,
  maxBufferedPlaybackPosition,
  instanceRef,
  removeEventListener,
  getCurrentTime,
  setCurrentTime,
  seekToFrameAccurate,
  videoRef,
}: UseSegmentedVideoPlayerInstanceProps) {
  useImperativeHandle(
    instanceRef,
    () => ({
      addEventListener,
      removeEventListener,

      pause() {
        return videoRef.current?.pause();
      },

      play() {
        return videoRef.current?.play();
      },

      get maxBufferedPlaybackPosition() {
        return maxBufferedPlaybackPosition();
      },

      get paused() {
        return videoRef.current?.paused;
      },

      get currentTime() {
        return getCurrentTime();
      },

      set currentTime(sec: number) {
        setCurrentTime(sec);
      },

      get readyState() {
        return videoRef.current?.readyState;
      },

      get videoHeight() {
        return videoRef.current?.videoHeight;
      },

      get videoWidth() {
        return videoRef.current?.videoWidth;
      },

      get dangerousHTMLVideoElement() {
        return videoRef.current;
      },

      get duration() {
        return millisToSec(getDurationMillis());
      },

      seekToFrameAccurate(logicalTimeSec: number) {
        return seekToFrameAccurate(logicalTimeSec);
      },
    }),
    [
      addEventListener,
      getCurrentTime,
      getDurationMillis,
      maxBufferedPlaybackPosition,
      removeEventListener,
      seekToFrameAccurate,
      setCurrentTime,
      videoRef,
    ],
  );
}
